import React, { useState, useEffect, useRef, useMemo } from 'react';
import { comboService } from '../../services/comboService';
import { productService } from '../../services/productService';
import type { Combo, CreateComboData, ComboProduct as ServiceComboProduct } from '../../services/comboService';
import type { Product } from '../../types';
import { getImageUrl } from '../../config';
import { toast } from 'react-toastify';
import { showConfirm, showError, showSuccess, showWarning } from '../../utils/sweetAlert';

// Función para truncar texto con puntos suspensivos
const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};



interface ComboProduct {
  product_id: number;
  quantity: number;
}

const CombosTab: React.FC = () => {
  // Agregar estilos CSS para la animación del spinner y scrollbar personalizado
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      /* Estilos para el scrollbar personalizado */
      .combo-products-container::-webkit-scrollbar {
        width: 8px;
      }
      
      .combo-products-container::-webkit-scrollbar-track {
        background: #f8f9fa;
        border-radius: 10px;
      }
      
      .combo-products-container::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%);
        border-radius: 10px;
        transition: all 0.3s ease;
      }
      
      .combo-products-container::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%);
        transform: scale(1.1);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
   }, []);

  const [combos, setCombos] = useState<Combo[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const [editingCombo, setEditingCombo] = useState<Combo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [comboSearchTerm, setComboSearchTerm] = useState('');
  const [comboPages, setComboPages] = useState<number[]>([]);

  const [comboProducts, setComboProducts] = useState<ComboProduct[]>([{ product_id: 0, quantity: 1 }]);
  const [showProductSearchSection, setShowProductSearchSection] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedComboProduct, setSelectedComboProduct] = useState<Product | null>(null);
  const [comboProductSearchTerm, setComboProductSearchTerm] = useState('');
  const [showComboProductDropdown, setShowComboProductDropdown] = useState(false);
  const [createdCombo, setCreatedCombo] = useState<{mainProduct: Product, comboProducts: Product[]} | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Efecto para manejar la búsqueda y paginación
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (comboSearchTerm.trim()) {
        // Si hay búsqueda, buscar globalmente
        console.log('Iniciando búsqueda global con término:', comboSearchTerm);
        fetchCombos(comboSearchTerm);
      } else {
        // Si no hay búsqueda, cargar página actual
        console.log('Cargando página normal:', currentPage);
        fetchCombos('');
      }
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [comboSearchTerm, currentPage]);

  // Efecto adicional para resetear la página cuando se limpia la búsqueda
  useEffect(() => {
    if (!comboSearchTerm.trim() && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [comboSearchTerm]);

  // Si hay término de búsqueda, mostrar solo los combos filtrados por el backend
  // Si no hay búsqueda, mostrar todos los combos de la página actual
  const filteredCombos = combos;

  const searchCombosAndFindPages = async (searchTerm: string) => {
    try {
      // Buscar en todos los combos para encontrar en qué páginas están
      const response = await comboService.getCombos(1, 1000, searchTerm);
      const foundCombos = response.combos;
      
      if (foundCombos.length === 0) {
        setComboPages([]);
        return;
      }
      
      // Obtener todos los combos sin filtro para determinar las páginas
      const allCombosResponse = await comboService.getCombos(1, 1000, '');
      const allCombos = allCombosResponse.combos;
      
      // Encontrar en qué páginas están los combos encontrados
      const pages = new Set<number>();
      const combosPerPage = 3;
      
      foundCombos.forEach(foundCombo => {
        const index = allCombos.findIndex(combo => combo.id === foundCombo.id);
        if (index !== -1) {
          const page = Math.floor(index / combosPerPage) + 1;
          pages.add(page);
        }
      });
      
      setComboPages(Array.from(pages).sort((a, b) => a - b));
    } catch (error) {
      console.error('Error searching combos:', error);
      setComboPages([]);
    }
  };

  const fetchCombos = async (searchTerm = '') => {
    try {
      setLoading(true);
      
      // Limpiar combos antes de la búsqueda para evitar que se mezclen resultados
      setCombos([]);
      
      // Si hay término de búsqueda, buscar en todos los combos (página 1, límite alto)
      // Si no hay búsqueda, usar paginación normal
      const page = searchTerm ? 1 : currentPage;
      const limit = searchTerm ? 1000 : 3; // Límite alto para búsqueda global
      
      console.log('Buscando combos con término:', searchTerm, 'página:', page, 'límite:', limit);
      
      const response = await comboService.getCombos(page, limit, searchTerm);
      console.log('Respuesta del backend:', response);
      console.log('Combos obtenidos:', response.combos.length, 'combos');
      
      setCombos(response.combos);
      
      // Solo actualizar totalPages si no estamos buscando
      if (!searchTerm) {
        setTotalPages(response.totalPages);
        setComboPages([]); // Limpiar páginas de búsqueda
      } else {
        // Para búsquedas, buscar en qué páginas están los combos
        await searchCombosAndFindPages(searchTerm);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching combos:', error);
      setCombos([]); // Limpiar en caso de error
      setComboPages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts(1, 100);
      setProducts(response.products);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };



  const handleEditCombo = (combo: Combo) => {
    setEditingCombo(combo);
    
    // Separar el primer producto (producto principal) del resto
    const mainProduct = combo.products?.[0] || null;
    const additionalProducts = combo.products?.slice(1) || [];
    
    // Establecer el producto principal en el buscador independiente
    if (mainProduct) {
      // Convertir ServiceComboProduct a Product
       const productForSelector: Product = {
         id: String(mainProduct.id),
         name: mainProduct.name,
         description: mainProduct.description || '',
         image_url: mainProduct.image_url || '',
         brand: mainProduct.brand || '',
         model: mainProduct.model || '',
         stock: mainProduct.stock,
         category_name: mainProduct.category_name || '',
         category_id: '0', // Valor por defecto como string
         featured: false, // Valor por defecto
         created_at: '', // Valor por defecto
         price: 0 // Valor por defecto
       };
      setSelectedComboProduct(productForSelector);
      setComboProductSearchTerm(mainProduct.name);
    } else {
      setSelectedComboProduct(null);
      setComboProductSearchTerm('');
    }
    

    // Solo establecer los productos adicionales en comboProducts (excluyendo el producto principal)
    setComboProducts(additionalProducts.map(p => ({ product_id: Number(p.id), quantity: p.quantity || 1 })));
    
    // Mostrar la sección de productos adicionales si hay productos adicionales
    setShowProductSearchSection(additionalProducts.length > 0);
    

  };

  const handleAddProduct = () => {
    setComboProducts(prev => [...prev, { product_id: 0, quantity: 1 }]);
  };

  const handleProductChange = (index: number, field: string, value: number) => {
    setComboProducts(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const handleRemoveProduct = (index: number) => {
    setComboProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteCombo = async (id: number) => {
    const confirmed = await showConfirm('¿Estás seguro de que quieres eliminar este combo?');
      if (confirmed) {
      try {
        await comboService.deleteCombo(id);
        toast.success('Combo eliminado exitosamente');
        fetchCombos();
      } catch (error) {
        console.error('Error deleting combo:', error);
        toast.error('Error al eliminar el combo');
      }
    }
  };



  const generateComboName = (selectedProductIds: string[]) => {
    const selectedProductNames = products
      .filter(p => selectedProductIds.includes(p.id))
      .map(p => p.name)
      .slice(0, 3);
    
    if (selectedProductNames.length === 0) return 'Combo Sin Productos';
    if (selectedProductNames.length <= 2) {
      return `Combo ${selectedProductNames.join(' + ')}`;
    }
    return `Combo ${selectedProductNames.slice(0, 2).join(' + ')} y más`;
  };

  const generateComboDescription = (selectedProductIds: string[]) => {
    const selectedProductNames = products
      .filter(p => selectedProductIds.includes(p.id))
      .map(p => p.name);
    
    if (selectedProductNames.length === 0) return 'Combo sin productos seleccionados';
    return `Combo que incluye: ${selectedProductNames.join(', ')}`;
  };



  const ProductSelector: React.FC<{
    products: Product[];
    selectedProductId: number;
    onProductChange: (productId: number) => void;
    placeholder?: string;
  }> = ({ products, selectedProductId, onProductChange, placeholder = "Selecciona producto" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const selectorRef = useRef<HTMLDivElement>(null);
    
    const selectedProduct = products.find(p => Number(p.id) === selectedProductId);
    
    const filteredProducts = useMemo(() => {
      if (!searchTerm.trim()) {
        return products.slice(0, 10);
      }
      return products.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.brand && product.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.model && product.model.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }, [products, searchTerm]);
    
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setIsOpen(true);
    };
    
    const handleProductSelect = (product: Product) => {
      onProductChange(Number(product.id));
      setSearchTerm('');
      setIsOpen(false);
    };
    
    const handleInputFocus = () => {
      setIsOpen(true);
    };
    
    return (
      <div ref={selectorRef} style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          value={selectedProduct ? selectedProduct.name : searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    fontSize: '14px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <div style={{ fontWeight: '500' }}>{product.name}</div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    marginTop: '2px'
                  }}>ID: {product.id}</div>
                </div>
              ))
            ) : (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: '#666',
                fontSize: '14px'
              }}>
                No se encontraron productos
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleProductSearchSelect = (product: Product) => {
    setComboProducts(prev => [...prev, { product_id: Number(product.id), quantity: 1 }]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
    // Mantener la sección de búsqueda visible para seguir agregando productos
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.position-relative')) {
        setShowProductDropdown(false);
        setShowComboProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredProductsForSearch = useMemo(() => {
    if (!productSearchTerm.trim()) return [];
    return products.filter(product => 
      product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      (product.brand && product.brand.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
      (product.model && product.model.toLowerCase().includes(productSearchTerm.toLowerCase()))
    ).slice(0, 10);
  }, [products, productSearchTerm]);

  const filteredComboProducts = useMemo(() => {
    if (!comboProductSearchTerm.trim()) return [];
    return products.filter(product => 
      product.name.toLowerCase().includes(comboProductSearchTerm.toLowerCase()) ||
      (product.brand && product.brand.toLowerCase().includes(comboProductSearchTerm.toLowerCase())) ||
      (product.model && product.model.toLowerCase().includes(comboProductSearchTerm.toLowerCase()))
    ).slice(0, 10);
  }, [products, comboProductSearchTerm]);

  return (
    <div className="combos-tab">
      <h3 className="mb-4">Gestión de Combos</h3>
      
      {/* Layout principal con contenedores separados */}
      <div style={{ display: 'flex', gap: '30px', marginBottom: '20px', alignItems: 'flex-start' }}>
        {/* Contenedor izquierdo - Buscador independiente y productos seleccionados */}
        <div style={{ 
          flex: '1', 
          maxWidth: '450px',
          padding: '20px'
        }}>
          <h5 style={{ margin: 0, color: '#242A5B', fontWeight: '700', fontSize: '18px', marginBottom: '20px' }}>Producto para Combo</h5>
          
          <div style={{ position: 'relative' }} className="position-relative">
            <input
              type="text"
              placeholder="🔍 Buscar producto específico..."
              value={comboProductSearchTerm}
              onChange={(e) => {
                setComboProductSearchTerm(e.target.value);
                setShowComboProductDropdown(e.target.value.length > 0);
              }}
              onFocus={() => setShowComboProductDropdown(comboProductSearchTerm.length > 0)}
              style={{
                width: '100%',
                padding: '14px 18px',
                border: '2px solid #e9ecef',
                borderRadius: '10px',
                fontSize: '15px',
                outline: 'none',
                transition: 'all 0.3s ease',
                background: '#ffffff',
                fontFamily: 'inherit'
              }}
            />
            
            {showComboProductDropdown && filteredComboProducts.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #e9ecef',
                borderRadius: '10px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: '320px',
                overflowY: 'auto',
                marginTop: '8px'
              }}>
                {filteredComboProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => {
                      setSelectedComboProduct(product);
                      setComboProductSearchTerm(product.name);
                      setShowComboProductDropdown(false);
                    }}
                    style={{
                      padding: '14px 18px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <img
                                src={getImageUrl(product.image_url || null)}
                                alt={product.name}
                                style={{
                                  width: '45px',
                                  height: '45px',
                                  objectFit: 'cover',
                                  borderRadius: '8px'
                                }}
                              />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>{product.name}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                        {product.brand} - {product.model}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Producto seleccionado */}
          {selectedComboProduct && !createdCombo && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)',
              borderRadius: '12px',
              border: '2px solid #28a745'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <img
                  src={getImageUrl(selectedComboProduct.image_url || null)}
                  alt={selectedComboProduct.name}
                  style={{
                    width: '55px',
                    height: '55px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '2px solid #28a745'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', color: '#155724', fontSize: '15px' }}>{selectedComboProduct.name}</div>
                  <div style={{ fontSize: '13px', color: '#155724', marginTop: '2px' }}>
                    {selectedComboProduct.brand} - {selectedComboProduct.model}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedComboProduct(null);
                    setComboProductSearchTerm('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Botón para limpiar combo creado */}
              <div style={{
                marginTop: '15px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => {
                    setCreatedCombo(null);
                    setSelectedComboProduct(null);
                    setComboProductSearchTerm('');
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#c82333';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                  }}
                >
                  <i className="fas fa-trash" style={{ marginRight: '6px' }}></i>
                  Limpiar Combo
                </button>
              </div>
            </div>
          )}
          
          {/* Combo creado */}
          {createdCombo && (
            <div style={{
              marginTop: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #fff3cd 0%, #FDBF37 100%)',
              borderRadius: '15px',
              border: '3px solid #FDBF37',
              boxShadow: '0 8px 25px rgba(255, 193, 7, 0.3)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '15px'
              }}>
                <div style={{
                  background: '#FDBF37',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  COMBO CREADO
                </div>
                <button
                  onClick={() => {
                    setCreatedCombo(null);
                    setSelectedComboProduct(null);
                    setComboProductSearchTerm('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    fontSize: '20px',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                    marginLeft: 'auto'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Producto principal */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px',
                background: 'white',
                borderRadius: '12px',
                border: '2px solid #FDBF37',
                marginBottom: '15px'
              }}>
                <img
                  src={getImageUrl(createdCombo.mainProduct.image_url || null)}
                  alt={createdCombo.mainProduct.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    border: '2px solid #FDBF37'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', color: '#856404', fontSize: '16px' }}>
                    {createdCombo.mainProduct.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#856404', marginTop: '2px' }}>
                    ID: {createdCombo.mainProduct.id}
                  </div>
                </div>
              </div>
              
              {/* Símbolo + */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '10px 0'
              }}>
                <div style={{
                  background: '#FDBF37',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: '700',
                  boxShadow: '0 4px 10px rgba(255, 193, 7, 0.3)'
                }}>
                  +
                </div>
              </div>
              
              {/* Productos del combo */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {createdCombo.comboProducts.map((product, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 193, 7, 0.3)'
                  }}>
                    <img
                      src={getImageUrl(product.image_url || null)}
                      alt={product.name}
                      style={{
                        width: '45px',
                        height: '45px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#856404', fontSize: '14px' }}>
                        {product.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#856404', marginTop: '1px' }}>
                        ID: {product.id}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Botones de acción - se muestran si hay productos seleccionados */}
          {comboProducts.length > 0 && comboProducts.some(p => p.product_id > 0) && (
            <div style={{
              marginTop: '20px'
            }}>
              
              {editingCombo ? (
                // Botones para editar combo
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={async () => {
                      const validProducts = comboProducts.filter(p => p.product_id > 0);
                      if (validProducts.length === 0) {
                        await showWarning('Debes seleccionar al menos un producto adicional');
                        return;
                      }
                      if (!selectedComboProduct) {
                        await showWarning('Debes seleccionar un producto principal para el combo');
                        return;
                      }
                      
                      try {
                        // Preparar datos para la API - incluir el producto principal como primer producto
                        const mainProductData = {
                          product_id: Number(selectedComboProduct.id),
                          quantity: 1
                        };
                        
                        // Combinar producto principal con productos adicionales
                        const allProducts = [mainProductData, ...validProducts];
                        
                        // Generar nombre y descripción del combo
                        const comboProductsList = validProducts.map(vp => 
                          products.find(p => Number(p.id) === vp.product_id)
                        ).filter(Boolean) as Product[];
                        
                        const comboName = `Combo ${selectedComboProduct.name} + ${comboProductsList.length} productos`;
                        const comboDescription = `Combo que incluye: ${selectedComboProduct.name}, ${comboProductsList.map(p => p.name).join(', ')}`;
                        
                        // Actualizar el combo en la base de datos
                        const comboData: CreateComboData = {
                          name: comboName,
                          description: comboDescription,
                          active: editingCombo.active,
                          products: allProducts
                        };
                        
                        await comboService.updateCombo(editingCombo.id, comboData);
                        
                        // Limpiar estados
                        setEditingCombo(null);
                        setSelectedComboProduct(null);
                        setComboProductSearchTerm('');
                        setComboProducts([]);
                        setShowProductSearchSection(false);
                        
                        // Actualizar la lista de combos
                        fetchCombos();
                        
                        await showSuccess('¡Combo actualizado exitosamente!');
                        
                      } catch (error) {
                        console.error('Error al actualizar combo:', error);
                        await showError('Error al actualizar el combo. Por favor, intenta nuevamente.');
                      }
                    }}
                    style={{
                      flex: '1',
                      padding: '15px 25px',
                      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 123, 255, 0.3)';
                    }}
                  >
                    <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>
                    Actualizar Combo
                  </button>
                  
                  <button
                    onClick={() => {
                      // Limpiar estados de edición
                      setEditingCombo(null);
                      setSelectedComboProduct(null);
                      setComboProductSearchTerm('');
                      setComboProducts([]);
                      setShowProductSearchSection(false);
                    }}
                    style={{
                      flex: '1',
                      padding: '15px 25px',
                      background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(108, 117, 125, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(108, 117, 125, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(108, 117, 125, 0.3)';
                    }}
                  >
                    <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                    Cancelar
                  </button>
                </div>
              ) : (
                // Botón para crear combo
                <button
                  onClick={async () => {
                    const validProducts = comboProducts.filter(p => p.product_id > 0);
                    if (validProducts.length === 0) {
                      await showWarning('Debes seleccionar al menos un producto');
                      return;
                    }
                    if (!selectedComboProduct) {
                      await showWarning('Debes seleccionar un producto principal para el combo');
                      return;
                    }
                    
                    try {
                      // Crear el combo con el producto principal y los productos agregados
                      const comboProductsList = validProducts.map(vp => 
                        products.find(p => Number(p.id) === vp.product_id)
                      ).filter(Boolean) as Product[];
                      
                      // Preparar datos para la API - incluir el producto principal como primer producto
                      const mainProductData = {
                        product_id: Number(selectedComboProduct.id),
                        quantity: 1
                      };
                      
                      // Combinar producto principal con productos adicionales
                      const allProducts = [mainProductData, ...validProducts];
                      
                      // Generar nombre y descripción del combo
                      const comboName = `Combo ${selectedComboProduct.name} + ${comboProductsList.length} productos`;
                      const comboDescription = `Combo que incluye: ${selectedComboProduct.name}, ${comboProductsList.map(p => p.name).join(', ')}`;
                      
                      // Crear el combo en la base de datos
                      const comboData: CreateComboData = {
                        name: comboName,
                        description: comboDescription,
                        active: true,
                        products: allProducts
                      };
                      
                      const savedCombo = await comboService.createCombo(comboData);
                      
                      // Actualizar el estado local para mostrar el combo creado
                      setCreatedCombo({
                        mainProduct: selectedComboProduct,
                        comboProducts: comboProductsList
                      });
                      
                      // Limpiar los productos seleccionados
                      setComboProducts([]);
                      
                      // Actualizar la lista de combos
                      fetchCombos();
                      
                      await showSuccess('¡Combo creado exitosamente!');
                      
                      console.log('Combo guardado en BD:', savedCombo);
                    } catch (error) {
                      console.error('Error al crear combo:', error);
                      await showError('Error al crear el combo. Por favor, intenta nuevamente.');
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '15px 25px',
                    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(40, 167, 69, 0.3)';
                  }}
                >
                  <i className="fas fa-magic" style={{ marginRight: '8px' }}></i>
                  Crear Combo Ahora
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Contenedor derecho - Botón y buscadores */}
        <div style={{ 
          flex: '1',
          padding: '20px'
        }}>
          <h5 style={{ margin: 0, color: '#242A5B', fontWeight: '700', fontSize: '18px', marginBottom: '20px' }}>Gestión de Productos</h5>
          
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setShowProductSearchSection(!showProductSearchSection)}
              style={{
                background: 'linear-gradient(135deg, #242A5B, #2c3e50)',
                border: 'none',
                borderRadius: '12px',
                padding: '18px 36px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(36, 42, 91, 0.3)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(36, 42, 91, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(36, 42, 91, 0.3)';
              }}
            >
              <i className="fas fa-plus" style={{ fontSize: '16px' }}></i>
              Agregar Productos al Combo
            </button>
            
            {/* Sección de búsqueda de productos - aparece debajo del botón */}
            {showProductSearchSection && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                border: '2px solid #e9ecef',
                borderRadius: '12px',
                background: '#ffffff'
              }}>
                <h6 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontWeight: '600' }}>Buscar y Agregar Productos</h6>
                
                <div style={{ position: 'relative' }} className="position-relative">
                  {/* Campo de búsqueda con productos seleccionados como chips */}
                  <div style={{
                    width: '100%',
                    minHeight: '54px',
                    border: '2px solid #e9ecef',
                    borderRadius: '10px',
                    background: '#ffffff',
                    padding: '8px 12px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'text'
                  }}>
                    {/* Chips de productos seleccionados */}
                    {comboProducts.filter(p => p.product_id > 0).map((comboProduct, index) => {
                      const product = products.find(p => Number(p.id) === comboProduct.product_id);
                      return product ? (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%)',
                          border: '1px solid #28a745',
                          borderRadius: '20px',
                          padding: '4px 8px 4px 4px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#2c3e50'
                        }}>
                          <img 
                            src={getImageUrl(product.image_url || null)} 
                            alt={product.name}
                            style={{
                              width: '24px',
                              height: '24px',
                              objectFit: 'cover',
                              borderRadius: '50%',
                              border: '1px solid #28a745'
                            }}
                          />
                          <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.name}
                          </span>
                          <button
                            onClick={() => {
                              setComboProducts(prev => prev.filter(p => p.product_id !== comboProduct.product_id));
                            }}
                            style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              border: 'none',
                              background: '#dc3545',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#c82333';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : null;
                    })}
                    
                    {/* Input de búsqueda */}
                    <input
                      type="text"
                      placeholder={comboProducts.filter(p => p.product_id > 0).length === 0 ? "🔍 Buscar productos por nombre, marca o modelo..." : "Buscar más productos..."}
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        setShowProductDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowProductDropdown(productSearchTerm.length > 0)}
                      style={{
                        flex: 1,
                        minWidth: '200px',
                        border: 'none',
                        outline: 'none',
                        fontSize: '16px',
                        background: 'transparent',
                        padding: '8px 4px'
                      }}
                    />
                  </div>
                  
                  {showProductDropdown && filteredProductsForSearch.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '2px solid #e9ecef',
                      borderTop: 'none',
                      borderRadius: '0 0 10px 10px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
                      {filteredProductsForSearch.map(product => (
                        <div
                          key={product.id}
                          onClick={() => handleProductSearchSelect(product)}
                          style={{
                            padding: '15px 20px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f8f9fa',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'translateX(5px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <img 
                            src={getImageUrl(product.image_url || null)} 
                            alt={product.name}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #e9ecef'
                            }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: '600', 
                              fontSize: '16px',
                              color: '#2c3e50',
                              marginBottom: '4px'
                            }}>{product.name}</div>
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#6c757d'
                            }}>ID: {product.id}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      
      {/* Sección de Combos Creados */}
      <div style={{ 
        marginTop: '30px', 
        padding: '25px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '25px'
        }}>
          <h4 style={{ 
            margin: '0',
            color: '#2d3436', 
            fontWeight: '700',
            fontSize: '24px'
          }}>🎯 Combos Creados</h4>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <input
              type="text"
              placeholder="Buscar combo por producto principal (búsqueda global)..."
              value={comboSearchTerm}
              onChange={(e) => setComboSearchTerm(e.target.value)}
              style={{
                padding: '10px 15px',
                border: '2px solid #ddd',
                borderRadius: '10px',
                fontSize: '14px',
                width: '280px',
                outline: 'none',
                transition: 'all 0.3s ease',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2A3A53';
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.boxShadow = '0 4px 12px rgba(42, 58, 83, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
                e.target.style.backgroundColor = '#f9f9f9';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              }}
            />
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2A3A53"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
        </div>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #007bff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <p style={{ marginTop: '10px', color: '#6c757d' }}>Cargando...</p>
          </div>
        ) : combos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#6c757d', fontSize: '16px' }}>No hay combos disponibles</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', maxWidth: '1000px', margin: '0 auto' }}>
             {filteredCombos.map(combo => (
               <div key={combo.id} style={{ 
                 background: 'linear-gradient(135deg, #fff3cd 0%, #FDBF37 100%)',
                 borderRadius: '12px',
                 border: '2px solid #FDBF37',
                 boxShadow: '0 6px 20px rgba(255, 193, 7, 0.25)',
                 padding: '16px',
                 transition: 'all 0.3s ease',
                 position: 'relative',
                 overflow: 'hidden',
                 minHeight: '320px',
                 display: 'flex',
                 flexDirection: 'column'
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.transform = 'translateY(-5px)';
                 e.currentTarget.style.boxShadow = '0 12px 35px rgba(255, 193, 7, 0.5)';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.transform = 'translateY(0)';
                 e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 193, 7, 0.3)';
               }}>
                <div style={{ 
                   position: 'absolute',
                   top: '0',
                   left: '0',
                   right: '0',
                   height: '4px',
                   background: 'linear-gradient(90deg, #FDBF37, #fff3cd, #FDBF37)'
                 }}></div>
                 <div style={{ marginBottom: '12px' }}>
                   <h5 style={{ 
                     margin: '0 0 6px 0', 
                     color: '#856404', 
                     fontWeight: '700',
                     fontSize: '16px'
                   }}>{truncateText(combo.name, 25)}</h5>

                 </div>
                
                {combo.products && combo.products.length > 0 && (
                  <div style={{ marginBottom: '12px', flex: 1 }}>
                     {/* Producto principal */}
                     {combo.products[0] && (
                       <div style={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: '12px',
                         padding: '12px',
                         background: 'white',
                         borderRadius: '10px',
                         border: '2px solid #FDBF37',
                         marginBottom: '12px'
                       }}>
                         <img
                            src={getImageUrl(combo.products[0].image_url || null)}
                            alt={combo.products[0].name}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #FDBF37'
                            }}
                          />
                         <div style={{ flex: 1 }}>
                           <div style={{ fontWeight: '700', color: '#856404', fontSize: '14px' }}>
                             {truncateText(combo.products[0].name, 20)}
                           </div>
                           <div style={{ fontSize: '12px', color: '#856404', marginTop: '2px' }}>
                             ID: {combo.products[0].id}
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {/* Símbolo + si hay más productos */}
                     {combo.products.length > 1 && (
                       <div style={{
                         display: 'flex',
                         justifyContent: 'center',
                         alignItems: 'center',
                         margin: '10px 0'
                       }}>
                         <div style={{
                           background: '#FDBF37',
                           color: 'white',
                           width: '32px',
                           height: '32px',
                           borderRadius: '50%',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           fontSize: '16px',
                           fontWeight: '700',
                           boxShadow: '0 3px 8px rgba(255, 193, 7, 0.3)'
                         }}>
                           +
                         </div>
                       </div>
                     )}
                     
                     {/* Productos adicionales */}
                     {combo.products.length > 1 && (
                       <div 
                         className="combo-products-container"
                         style={{
                           display: 'flex',
                           flexDirection: 'column',
                           gap: '8px',
                           maxHeight: '180px', // Altura reducida para mostrar aproximadamente 2-3 productos
                           overflowY: 'auto',
                           paddingRight: '5px',
                           // Estilos para el scrollbar
                           scrollbarWidth: 'thin',
                           scrollbarColor: '#FDBF37 #f8f9fa'
                         }}>
                         {combo.products.slice(1).map((product, index) => (
                           <div key={index + 1} style={{
                             display: 'flex',
                             alignItems: 'center',
                             gap: '10px',
                             padding: '10px',
                             background: 'rgba(255, 255, 255, 0.7)',
                             borderRadius: '8px',
                             border: '1px solid rgba(255, 193, 7, 0.3)',
                             minHeight: '60px' // Altura reducida para cada producto
                           }}>
                             <img
                               src={getImageUrl(product.image_url || null)}
                               alt={product.name}
                               style={{
                                 width: '40px',
                                 height: '40px',
                                 objectFit: 'cover',
                                 borderRadius: '6px'
                               }}
                             />
                             <div style={{ flex: 1 }}>
                               <div style={{ fontWeight: '600', color: '#856404', fontSize: '13px' }}>
                                 {truncateText(product.name, 18)}
                               </div>
                               <div style={{ fontSize: '11px', color: '#856404', marginTop: '1px' }}>
                                 ID: {product.id}
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                   <button
                     onClick={() => handleEditCombo(combo)}
                     style={{
                       flex: 1,
                       padding: '10px 15px',
                       background: '#3498DB',
                       color: 'white',
                       border: 'none',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600',
                       cursor: 'pointer',
                       transition: 'all 0.3s ease',
                       boxShadow: '0 3px 10px rgba(52, 152, 219, 0.4)',
                       textTransform: 'uppercase',
                       letterSpacing: '0.5px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '6px'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.transform = 'translateY(-2px)';
                       e.currentTarget.style.boxShadow = '0 5px 15px rgba(52, 152, 219, 0.6)';
                       e.currentTarget.style.background = '#2980B9';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.transform = 'translateY(0)';
                       e.currentTarget.style.boxShadow = '0 3px 10px rgba(52, 152, 219, 0.4)';
                       e.currentTarget.style.background = '#3498DB';
                     }}
                   >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                       <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                     </svg>
                     Editar
                   </button>
                   <button
                     onClick={() => handleDeleteCombo(combo.id)}
                     style={{
                       flex: 1,
                       padding: '10px 15px',
                       background: '#E74C3C',
                       color: 'white',
                       border: 'none',
                       borderRadius: '8px',
                       fontSize: '14px',
                       fontWeight: '600',
                       cursor: 'pointer',
                       transition: 'all 0.3s ease',
                       boxShadow: '0 3px 10px rgba(231, 76, 60, 0.4)',
                       textTransform: 'uppercase',
                       letterSpacing: '0.5px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       gap: '6px'
                     }}
                     onMouseEnter={(e) => {
                       e.currentTarget.style.transform = 'translateY(-2px)';
                       e.currentTarget.style.boxShadow = '0 5px 15px rgba(231, 76, 60, 0.6)';
                       e.currentTarget.style.background = '#c0392b';
                     }}
                     onMouseLeave={(e) => {
                       e.currentTarget.style.transform = 'translateY(0)';
                       e.currentTarget.style.boxShadow = '0 3px 10px rgba(231, 76, 60, 0.4)';
                       e.currentTarget.style.background = '#E74C3C';
                     }}
                   >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                       <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                     </svg>
                     Eliminar
                   </button>
                 </div>
              </div>
            ))}
          </div>
          
          {/* Indicador de página con flechas */}
          {comboSearchTerm.trim() && comboPages.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px'
            }}>
              {/* Botón Anterior */}
              <button
                onClick={() => {}}
                disabled={true}
                style={{
                  padding: '8px 12px',
                  background: '#9E9E9E',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'not-allowed',
                  transition: 'all 0.3s ease',
                  boxShadow: 'none'
                }}
              >
                ← Anterior
              </button>
              
              {/* Círculos de páginas */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {comboPages.map((page, index) => (
                  <div key={page} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {index > 0 && (
                      <svg 
                        style={{ width: '16px', height: '16px', color: '#FDBF37' }} 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                    )}
                    <button
                      onClick={() => {
                        setComboSearchTerm('');
                        setCurrentPage(page);
                      }}
                      style={{
                        width: '40px',
                        height: '40px',
                        background: currentPage === page 
                          ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                          : 'linear-gradient(135deg, #fff3cd 0%, #FDBF37 100%)',
                        color: currentPage === page ? 'white' : '#856404',
                        border: currentPage === page ? 'none' : '2px solid #FDBF37',
                        borderRadius: '50%',
                        fontSize: '14px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: currentPage === page 
                          ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                          : '0 2px 8px rgba(255, 193, 7, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 193, 7, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = currentPage === page 
                          ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                          : '0 2px 8px rgba(255, 193, 7, 0.2)';
                      }}
                    >
                      {page}
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Botón Siguiente */}
              <button
                onClick={() => {}}
                disabled={true}
                style={{
                  padding: '8px 12px',
                  background: '#9E9E9E',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'not-allowed',
                  transition: 'all 0.3s ease',
                  boxShadow: 'none'
                }}
              >
                Siguiente →
              </button>
            </div>
          )}
          
          {/* Paginación */}
          {totalPages > 1 && !comboSearchTerm.trim() && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '30px',
              padding: '20px'
            }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '10px 15px',
                  background: currentPage === 1 ? '#9E9E9E' : '#2A3A53',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: currentPage === 1 ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                    e.currentTarget.style.background = '#1e2a3a';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                    e.currentTarget.style.background = '#2A3A53';
                  }
                }}
              >
                ← Anterior
              </button>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: page === currentPage 
                        ? 'linear-gradient(135deg, #FDBF37 0%, #FDBF37 100%)' 
                        : 'white',
                      color: page === currentPage ? 'white' : '#2c3e50',
                      border: page === currentPage ? 'none' : '2px solid #e9ecef',
                      borderRadius: '50%',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: page === currentPage 
                        ? '0 4px 12px rgba(255, 193, 7, 0.4)' 
                        : '0 2px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (page !== currentPage) {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== currentPage) {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '10px 15px',
                  background: currentPage === totalPages ? '#9E9E9E' : '#2A3A53',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: currentPage === totalPages ? 'none' : '0 3px 10px rgba(42, 58, 83, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(42, 58, 83, 0.4)';
                    e.currentTarget.style.background = '#1e2a3a';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 3px 10px rgba(42, 58, 83, 0.3)';
                    e.currentTarget.style.background = '#2A3A53';
                  }
                }}
              >
                Siguiente →
              </button>
            </div>
          )}
          </>
        )}
      </div>


    </div>
  );
};

export default CombosTab;